import { FieldAwareness } from '../field/FieldAwareness'
import { FieldContext } from '../field/FieldContext'

// ==================== FIELD AWARENESS INTERFACE ====================

/**
 * Interface for POI objects that support Field Awareness
 */
export interface IPOIFieldAware {
  // Field properties
  index: number
  fieldAwareness: FieldAwareness | null
  
  // Field methods
  updateFromField(deltaTime: number): void
  setFieldContext(context: FieldContext): void
  enableFieldAwareness(context: FieldContext): void
  disableFieldAwareness(): void
  
  // Field state
  isFieldAware(): boolean
  getFieldIndex(): number
  setFieldIndex(index: number): void
}

// ==================== FIELD UPDATE CALLBACK ====================

/**
 * Callback for field updates
 */
export type FieldUpdateCallback = (poi: IPOIFieldAware, deltaTime: number) => void

// ==================== FIELD ANIMATION STATES ====================

/**
 * Animation states for field-aware POIs
 */
export enum FieldAnimationState {
  Static = 'static',
  Hovering = 'hovering',
  Rotating = 'rotating',
  Breathing = 'breathing',
  Flowing = 'flowing',
  Rippling = 'rippling'
}

// ==================== FIELD EVENTS ====================

/**
 * Events emitted by field-aware POIs
 */
export interface FieldEvents {
  onFieldEnabled: (poi: IPOIFieldAware) => void
  onFieldDisabled: (poi: IPOIFieldAware) => void
  onFieldUpdate: (poi: IPOIFieldAware, deltaTime: number) => void
  onFieldParameterChange: (poi: IPOIFieldAware, paramName: string, value: any) => void
  onFieldAnimationStart: (poi: IPOIFieldAware, animationType: FieldAnimationState) => void
  onFieldAnimationEnd: (poi: IPOIFieldAware, animationType: FieldAnimationState) => void
}

// ==================== FIELD CONFIGURATION ====================

/**
 * Configuration for field-aware POI behavior
 */
export interface FieldConfiguration {
  // Animation settings
  enableHover: boolean
  enableRotation: boolean
  enableScale: boolean
  
  // Performance settings
  updateFrequency: number // Updates per second
  maxUpdateDistance: number // Max distance for updates
  
  // Visual settings
  showFieldDebug: boolean
  showFieldGizmos: boolean
  
  // Interaction settings
  respondToInteraction: boolean
  pauseOnHover: boolean
  
  // Custom callbacks
  onUpdate?: FieldUpdateCallback
  onFieldChange?: (oldContext: FieldContext | null, newContext: FieldContext) => void
}

// ==================== DEFAULT CONFIGURATION ====================

/**
 * Default field configuration
 */
export const DEFAULT_FIELD_CONFIG: FieldConfiguration = {
  enableHover: true,
  enableRotation: true,
  enableScale: false,
  updateFrequency: 60,
  maxUpdateDistance: 1000,
  showFieldDebug: false,
  showFieldGizmos: false,
  respondToInteraction: true,
  pauseOnHover: false
}

// ==================== FIELD MIXIN ====================

/**
 * Mixin function to add field awareness to any object
 */
export function addFieldAwareness<T extends object>(target: T): T & IPOIFieldAware {
  const fieldAwareTarget = target as T & IPOIFieldAware
  
  // Initialize field properties
  fieldAwareTarget.index = 0
  fieldAwareTarget.fieldAwareness = null
  
  // Add field methods
  fieldAwareTarget.updateFromField = function(deltaTime: number): void {
    if (this.fieldAwareness) {
      this.fieldAwareness.update(this, deltaTime)
    }
  }
  
  fieldAwareTarget.setFieldContext = function(context: FieldContext): void {
    if (this.fieldAwareness) {
      this.fieldAwareness.updateContext(context)
    }
  }
  
  fieldAwareTarget.enableFieldAwareness = function(context: FieldContext): void {
    this.fieldAwareness = new FieldAwareness(context)
  }
  
  fieldAwareTarget.disableFieldAwareness = function(): void {
    this.fieldAwareness = null
  }
  
  fieldAwareTarget.isFieldAware = function(): boolean {
    return this.fieldAwareness !== null
  }
  
  fieldAwareTarget.getFieldIndex = function(): number {
    return this.index
  }
  
  fieldAwareTarget.setFieldIndex = function(index: number): void {
    this.index = index
  }
  
  return fieldAwareTarget
}

// ==================== FIELD UTILITIES ====================

/**
 * Utility functions for field-aware POIs
 */
export class FieldUtils {
  /**
   * Check if object is field-aware
   */
  static isFieldAware(obj: any): obj is IPOIFieldAware {
    return obj && 
           typeof obj.index === 'number' &&
           typeof obj.updateFromField === 'function' &&
           typeof obj.enableFieldAwareness === 'function'
  }
  
  /**
   * Sort POIs by field index
   */
  static sortByFieldIndex(pois: IPOIFieldAware[]): IPOIFieldAware[] {
    return pois.sort((a, b) => a.index - b.index)
  }
  
  /**
   * Assign sequential field indices
   */
  static assignSequentialIndices(pois: IPOIFieldAware[]): void {
    pois.forEach((poi, index) => {
      poi.setFieldIndex(index)
    })
  }
  
  /**
   * Get POIs in field range
   */
  static getPOIsInRange(
    pois: IPOIFieldAware[], 
    startIndex: number, 
    endIndex: number
  ): IPOIFieldAware[] {
    return pois.filter(poi => 
      poi.index >= startIndex && poi.index <= endIndex
    )
  }
  
  /**
   * Enable field awareness for all POIs
   */
  static enableFieldAwarenessForAll(pois: IPOIFieldAware[], context: FieldContext): void {
    pois.forEach(poi => {
      poi.enableFieldAwareness(context)
    })
  }
  
  /**
   * Disable field awareness for all POIs
   */
  static disableFieldAwarenessForAll(pois: IPOIFieldAware[]): void {
    pois.forEach(poi => {
      poi.disableFieldAwareness()
    })
  }
  
  /**
   * Update all field-aware POIs
   */
  static updateAllPOIs(pois: IPOIFieldAware[], deltaTime: number): void {
    pois.forEach(poi => {
      if (poi.isFieldAware()) {
        poi.updateFromField(deltaTime)
      }
    })
  }
} 